import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SortableList, { DragHandle } from "../../components/SortableList";

const BLANK = { slug: "", name: "", name_en: "", name_de: "", description: "", icon: "Package", image: "", order: 0, active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/categories", { params: { active_only: false } }).then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, order: Number(form.order || 0) };
    try {
      if (editingId) await api.put(`/admin/categories/${editingId}`, body);
      else await api.post("/admin/categories", body);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const persistReorder = async (newCats) => {
    setCats(newCats);
    try {
      await api.put(`/admin/categories/_reorder`, { ids: newCats.map((c) => c.id) });
      toast.success("Sıralama güncellendi");
    } catch { toast.error("Sıra kaydedilemedi"); load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-serif text-3xl font-semibold text-navy">Kategoriler</h1>
        <Button onClick={() => { setForm(BLANK); setEditingId(null); setOpen(true); }} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-category-new">
          <Plus size={16} className="mr-1" /> Yeni Kategori
        </Button>
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="bg-softbg text-xs uppercase tracking-wider text-gray-500 px-3 py-3 grid grid-cols-[auto_1fr_1fr_80px_80px_120px] gap-3 items-center">
          <span></span><span>Kategori</span><span>Slug</span><span className="text-center">Sıra</span><span className="text-center">Aktif</span><span className="text-right">İşlem</span>
        </div>
        <div className="p-2">
          <SortableList items={cats} setItems={persistReorder} keyOf={(c) => c.id}>
            {(c, dragProps) => (
              <div className="grid grid-cols-[auto_1fr_1fr_80px_80px_120px] gap-3 items-center bg-white border border-gray-100 rounded-md px-3 py-2 mb-2 hover:bg-softbg/60" data-testid={`admin-cat-row-${c.slug}`}>
                <DragHandle {...dragProps} />
                <div className="flex items-center gap-3">
                  {c.image && <img src={c.image} alt="" className="h-8 w-8 rounded object-cover" />}
                  <span className="font-medium text-navy text-sm">{c.name}</span>
                </div>
                <span className="text-gray-500 text-xs">/{c.slug}</span>
                <span className="text-center text-sm">{c.order}</span>
                <span className="text-center">{c.active ? "●" : "○"}</span>
                <div className="text-right">
                  <button onClick={() => { setForm(c); setEditingId(c.id); setOpen(true); }} className="p-1.5 text-gray-500 hover:text-teal" data-testid={`admin-cat-edit-${c.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 text-gray-500 hover:text-rose-600" data-testid={`admin-cat-delete-${c.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="heading-serif">{editingId ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
              <div><Label className="text-xs">Ad (TR)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            </div>
            <div><Label className="text-xs">Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Kategori Görseli" testId="category-image" />
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Ikon (Lucide adı)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></div>
              <div><Label className="text-xs">Sıra</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
              <label className="flex items-center gap-2 pt-5 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-gold hover:bg-[#c49d58]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
